import { StrictMode } from "react";
import { BProgress } from "@bprogress/core";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";

import "@bprogress/core/css";
import "@roqua/components/styles/globals.css";

// Import the generated route tree
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import appsignal from "./appsignal/client";
import { toError } from "./lib/utils";
import { ErrorFallback } from "./components/ErrorBoundary";
import { routeTree } from "./routeTree.gen";

const queryClient = new QueryClient();

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: false,
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: ({ error }) => (
    <ErrorFallback error={error} resetErrorBoundary={() => null} />
  ),
  defaultOnCatch: (error) => {
    console.error("Error caught in router:", error);
    void appsignal.sendError(toError(error), {});
  },
});

BProgress.configure({
  showSpinner: false,
});

router.subscribe("onBeforeLoad", ({ fromLocation, pathChanged }) => {
  // Only show the progress bar on navigation (not initial page load), and only if the path has changed
  fromLocation && pathChanged && BProgress.start();
});

router.subscribe("onLoad", () => {
  BProgress.done();
});

router.subscribe("onBeforeNavigate", ({ toLocation }) => {
  appsignal.addBreadcrumb({
    category: "navigation",
    action: `Navigating to ${toLocation.href}`,
  });
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}
